import React from "react";
import { Search, SlidersHorizontal, List, Columns3, ArrowDownUp } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { MijnLocatiesSelect } from "@/components/MijnLocaties";
import { Checkbox } from "@/components/ui/checkbox";
import { OpvolgerAvatar } from "./Opvolger";
import { SORTS, GEEN_OPVOLGER, cap } from "./afsprakenUtils";

const seg = (on) => `flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm text-sm font-medium whitespace-nowrap transition-colors ${on ? "bg-zinc-950 text-white" : "text-zinc-500 hover:bg-zinc-100"}`;
const selCls = "w-full px-3 py-2 rounded-md border border-zinc-300 bg-white text-sm text-zinc-900 focus:outline-none focus:ring-2 focus:ring-blue-600";
const Lbl = ({ children }) => <label className="block text-[11px] uppercase tracking-wider font-semibold text-zinc-500 mb-1">{children}</label>;

const OpvolgerChip = ({ o, ik, value, onChange }) => (
  <button data-testid={`opvolger-filter-${o}`} onClick={() => onChange(o)} className={`shrink-0 ${seg(value === o)}`}>
    <OpvolgerAvatar name={o} size="h-5 w-5 text-[10px]" />{o === ik ? <span data-testid="opvolger-filter-mijn">Mijn afspraken</span> : <span className="capitalize">{o}</span>}
  </button>
);

const OpvolgerSegment = ({ oms, value, onChange, ik }) => (
  <div data-testid="opvolger-filter" className="tw1-scroll-x flex items-center gap-0.5 rounded-md border border-zinc-300 bg-white p-0.5 overflow-x-auto max-w-full">
    {/* v18-D7: voor een OM staat "Mijn afspraken" vooraan, dan Iedereen */}
    {oms.includes(ik) && <OpvolgerChip o={ik} ik={ik} value={value} onChange={onChange} />}
    <button data-testid="opvolger-filter-alle" onClick={() => onChange("alle")} className={`shrink-0 ${seg(value === "alle")}`}>Iedereen</button>
    {oms.filter((o) => o !== ik).map((o) => <OpvolgerChip key={o} o={o} ik={ik} value={value} onChange={onChange} />)}
    <button data-testid="opvolger-filter-geen" title="Geen (actieve) opvolger" onClick={() => onChange(GEEN_OPVOLGER)} className={seg(value === GEEN_OPVOLGER)}>
      <OpvolgerAvatar name="" size="h-5 w-5 text-[10px]" />Geen
    </button>
  </div>
);

const FiltersPopover = ({ f, set, isOm, user, locaties, metaError, oms, bedrijfsleiders, afdelingen = [], aantalActief }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button data-testid="afspraken-filters-btn" className={`flex items-center gap-1.5 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${aantalActief ? "bg-blue-50 border-blue-300 text-blue-800" : "bg-white border-zinc-300 text-zinc-700 hover:border-zinc-400"}`}>
        <SlidersHorizontal className="h-4 w-4" /> Filters
        {aantalActief > 0 && <span data-testid="afspraken-filters-badge" className="text-[10px] font-bold bg-blue-600 text-white rounded-full px-1.5 py-0.5 leading-none">{aantalActief}</span>}
      </button>
    </PopoverTrigger>
    <PopoverContent align="end" collisionPadding={8} className="w-72 space-y-3 max-h-[var(--radix-popover-content-available-height)] overflow-y-auto" data-testid="afspraken-filters-panel">
      <div><Lbl>Eigenaar</Lbl>
        <select data-testid="afspraak-filter-eigenaar" value={f.eigenaar} onChange={(e) => set.eigenaar(e.target.value)} className={selCls}>
          <option value="alle">Alle eigenaren</option><option value="bedrijfsleider">Bedrijfsleider</option><option value="om">OM</option><option value="beide">Beide</option><option value="afdeling">Afdeling</option>
        </select></div>
      {afdelingen.length > 0 && <div><Lbl>Afdeling</Lbl>
        <select data-testid="afspraak-filter-afdeling" value={f.afdeling} onChange={(e) => set.afdeling(e.target.value)} className={selCls}>
          <option value="alle">Alle afdelingen</option>
          {afdelingen.map((a) => <option key={a.id} value={a.id}>{a.naam}{a.actief === false ? " (niet actief)" : ""}</option>)}
        </select></div>}
      <div><Lbl>Bedrijfsleider</Lbl>
        <select data-testid="afspraak-filter-bedrijfsleider" value={f.bedrijfsleider} onChange={(e) => set.bedrijfsleider(e.target.value)} className={selCls}>
          <option value="">Alle bedrijfsleiders</option>{bedrijfsleiders.map((b) => <option key={b} value={b}>{b}</option>)}
        </select></div>
      {isOm && <div><Lbl>Mijn locaties</Lbl><MijnLocatiesSelect username={user.username} locations={locaties} error={!!metaError} /></div>}
      {oms.length > 0 && <div><Lbl>Locaties van OM</Lbl>
        <select data-testid="afspraak-filter-om" value={f.om} onChange={(e) => set.om(e.target.value)} className={selCls}>
          <option value="alle">Alle OM's</option>{oms.map((o) => <option key={o} value={o}>{cap(o)}</option>)}
        </select></div>}
      <div><Lbl>Reacties</Lbl>
        <label className="flex items-center gap-2 text-sm text-zinc-800 cursor-pointer">
          <Checkbox data-testid="afspraak-filter-besluit" checked={f.besluit} onCheckedChange={(v) => set.besluit(v === true)} /> Met besluit
        </label></div>
      <div><Lbl>Sorteren</Lbl>
        <div className="relative">
          <ArrowDownUp className="h-3.5 w-3.5 text-zinc-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <select data-testid="afspraak-sort" value={f.sort} onChange={(e) => set.sort(e.target.value)} className={`${selCls} pl-8`}>
            {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div></div>
    </PopoverContent>
  </Popover>
);

export const AfsprakenToolbar = ({ zoek, setZoek, opvolger, setOpvolger, view, setView, showView, oms, opvolgerOms, ...rest }) => (
  <div className="flex flex-wrap items-center gap-2 mb-4">
    <div className="relative flex-1 min-w-[200px] max-w-sm">
      <Search className="h-4 w-4 text-zinc-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
      <input data-testid="afspraken-zoek" value={zoek} onChange={(e) => setZoek(e.target.value)} placeholder="Zoek op wat, locatie, bedrijfsleider of reactie"
        className="w-full pl-8 pr-3 py-2 rounded-md border border-zinc-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
    </div>
    <OpvolgerSegment oms={opvolgerOms || oms} value={opvolger} onChange={setOpvolger} ik={rest.user?.username} />
    {showView && (
      <div className="flex items-center gap-0.5 rounded-md border border-zinc-300 bg-white p-0.5" data-testid="afspraak-view-toggle">
        <button data-testid="view-lijst" onClick={() => setView("lijst")} className={seg(view === "lijst")}><List className="h-4 w-4" />Lijst</button>
        <button data-testid="view-bord" onClick={() => setView("bord")} className={seg(view === "bord")}><Columns3 className="h-4 w-4" />Bord</button>
      </div>
    )}
    <FiltersPopover oms={oms} {...rest} />
  </div>
);
